/**
 * Super Admin Route guard
 *
 * Restricts access to super admin users only.
 * Redirects to sign-in if not authenticated, or to dashboard if not a super admin.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './ClerkAuthProvider';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SuperAdminRouteProps {
  children: ReactNode;
}

function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  );
}

export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const location = useLocation();
  const { isLoading, isAuthenticated, isSuperAdmin } = useAuthContext();

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (!isSuperAdmin) {
    toast.error('Access denied: Super admin required');
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
